/* ---------------------------------------------------------------------------------------------------------------
   JB MIX & MATCH — builder
   ---------------------------------------------------------------------------------------------------------------
   Adds ONE cart line: the bundle parent variant carrying `_bundleComponents`, which the "JuiceBox Bundle App"
   cart transform expands into the chosen variants at exactly the per-unit prices computed here.

   HARD RULES
   * All money is integer cents. No floats anywhere near a price.
   * compute() is pure: it derives everything from state.qty, so stepping back DOWN the ladder always restores
     the higher price.
   * Never emit `_bundleId` or `data-jb-bundle-remove` — those route the line into the LEGACY bundle renderer
     and arm a cart-wide welcome_kit purge.
--------------------------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  var root = document.querySelector('[data-jb-mm-root]');
  if (!root) return;

  var configEl = root.querySelector('[data-jb-mm-config]');
  if (!configEl) return;

  var CFG;
  try {
    CFG = JSON.parse(configEl.textContent);
  } catch (err) {
    console.error('[jb-mix-and-match] config parse failed', err);
    return;
  }

  if (!CFG.parentVariantId) {
    console.error('[jb-mix-and-match] no bundle parent variant configured — builder disabled');
    return;
  }

  // Tiers ascending; compute() walks them backwards to find the highest one reached.
  var TIERS = (CFG.tiers || []).slice().sort(function (a, b) {
    return a.min - b.min;
  });

  // Zubehör/Proben ladder — independent of the box price, keyed on the box count (2→10%, 3→20%, 4→30%).
  var ADDON_TIERS = (CFG.addonTiers || []).slice().sort(function (a, b) {
    return a.min - b.min;
  });

  function addonPctFor(boxCount) {
    var pct = 0;
    for (var i = 0; i < ADDON_TIERS.length; i++) {
      if (boxCount >= ADDON_TIERS[i].min) pct = ADDON_TIERS[i].pct || 0;
    }
    return pct;
  }

  function boxWord(count) {
    return count === 1 ? 'Box' : 'Dozen';
  }

  function giftUnlocked(boxCount) {
    return !!(CFG.gift && CFG.gift.minBoxes > 0 && boxCount >= CFG.gift.minBoxes);
  }

  function isGiftEligible(item) {
    return !!(item && item.available && item.group !== 'flavor' &&
      CFG.gift && CFG.gift.limitCents > 0 && item.price < CFG.gift.limitCents);
  }

  function nextAddonTier(boxCount) {
    for (var i = 0; i < ADDON_TIERS.length; i++) {
      if (boxCount < ADDON_TIERS[i].min) return ADDON_TIERS[i];
    }
    return null;
  }

  var STORAGE_KEY = 'jb-mm-v1';
  var MAX_THUMBS = 8;
  var MAX_PROPERTY_CHARS = 3500;
  var MAX_SLOTS = 5;

  var money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
  var fmt = function (cents) {
    return money.format(cents / 100);
  };

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── State ────────────────────────────────────────────────────────────────────────────────────────────── */

  // qty is the pricing truth; order is one entry PER UNIT in the order they were added, purely for the
  // tile row. Chronological append is what keeps existing tiles pixel-still when anything new arrives.
  var state = { qty: Object.create(null), order: [], freeGiftId: null };
  var inFlight = false;
  var scrollBeforeAdd = null;

  var el = {
    message: root.querySelector('[data-jb-mm-message]'),
    count: root.querySelector('[data-jb-mm-count]'),
    progress: root.querySelector('[data-jb-mm-progress]'),
    sparleiste: root.querySelector('[data-jb-mm-sparleiste]'),
    sentinel: root.querySelector('[data-jb-mm-sentinel]'),
    gates: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-gate]')),
    summary: root.querySelector('[data-jb-mm-summary]'),
    lines: root.querySelector('[data-jb-mm-lines]'),
    sheetLines: root.querySelector('[data-jb-mm-sheet-lines]'),
    subtotal: root.querySelector('[data-jb-mm-subtotal]'),
    saverow: root.querySelector('[data-jb-mm-saverow]'),
    savings: root.querySelector('[data-jb-mm-savings]'),
    total: root.querySelector('[data-jb-mm-total]'),
    savepill: root.querySelector('[data-jb-mm-savepill]'),
    dock: root.querySelector('[data-jb-mm-dock]'),
    slots: root.querySelector('[data-jb-mm-slots]'),
    howto: root.querySelector('[data-jb-mm-howto]'),
    shell: root.querySelector('.jb-mm'),
    dockTotal: root.querySelector('[data-jb-mm-dock-total]'),
    dockCompare: root.querySelector('[data-jb-mm-dock-compare]'),
    dockSave: root.querySelector('[data-jb-mm-dock-save]'),
    dockNudge: root.querySelector('[data-jb-mm-dock-nudge]'),
    dockProgress: root.querySelector('[data-jb-mm-dock-progress]'),
    ghostTpl: root.querySelector('template[data-jb-mm-ghost]'),
    sheet: root.querySelector('[data-jb-mm-sheet]'),
    sheetTotal: root.querySelector('[data-jb-mm-sheet-total]'),
    sheetCompare: root.querySelector('[data-jb-mm-sheet-compare]'),
    sheetSave: root.querySelector('[data-jb-mm-sheet-save]'),
    navItems: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-nav]')),
    steps: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-step]')),
    nearmiss: root.querySelector('[data-jb-mm-nearmiss]'),
    toast: root.querySelector('[data-jb-mm-toast]'),
    toastTitle: root.querySelector('[data-jb-mm-toast-title]'),
    toastSub: root.querySelector('[data-jb-mm-toast-sub]'),
    navBadges: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-nav-badge]')),
    intercept: root.querySelector('[data-jb-mm-intercept]'),
    interceptTitle: root.querySelector('[data-jb-mm-intercept-title]'),
    interceptText: root.querySelector('[data-jb-mm-intercept-text]'),
    live: root.querySelector('[data-jb-mm-live]'),
    rungs: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-rung]')),
    ctas: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-add]')),
    cards: Array.prototype.slice.call(root.querySelectorAll('[data-jb-mm-item]'))
  };

  var cardById = Object.create(null);
  el.cards.forEach(function (card) {
    cardById[card.getAttribute('data-jb-mm-item')] = card;
  });

  /* ── Price engine (pure) ──────────────────────────────────────────────────────────────────────────────── */

  function tierFor(boxCount) {
    for (var i = TIERS.length - 1; i >= 0; i--) {
      if (boxCount >= TIERS[i].min) return TIERS[i];
    }
    return null;
  }

  function nextTierFor(boxCount) {
    for (var i = 0; i < TIERS.length; i++) {
      if (boxCount < TIERS[i].min) return TIERS[i];
    }
    return null;
  }

  function unitPayCents(item, tier, boxCount) {
    if (item.group === 'flavor') {
      if (!tier) return item.price;
      return Math.max(0, item.price - (tier.offCents || 0));
    }
    var pct = addonPctFor(boxCount || 0);
    if (!pct) return item.price;
    return Math.round((item.price * (100 - pct)) / 100);
  }

  function compute() {
    var boxCount = 0;
    var ids = Object.keys(state.qty);
    var i;

    for (i = 0; i < ids.length; i++) {
      var it = CFG.items[ids[i]];
      if (it && it.group === 'flavor') boxCount += state.qty[ids[i]];
    }

    var tier = tierFor(boxCount);
    var next = nextTierFor(boxCount);

    var lines = [];
    var listCents = 0;
    var payCents = 0;

    for (i = 0; i < ids.length; i++) {
      var id = ids[i];
      var qty = state.qty[id];
      var item = CFG.items[id];
      if (!item || qty < 1) continue;

      var unit = unitPayCents(item, tier, boxCount);
      listCents += item.price * qty;
      payCents += unit * qty;

      lines.push({
        id: id,
        qty: qty,
        title: item.title,
        image: item.image,
        group: item.group,
        unitList: item.price,
        unitPay: unit,
        isGift: false
      });
    }

    // The gift is the customer's own pick — any eligible add-on/sample, free once the rung is reached.
    var gift = null;
    if (giftUnlocked(boxCount) && state.freeGiftId) {
      var giftItem = CFG.items[state.freeGiftId];
      if (isGiftEligible(giftItem)) {
        gift = giftItem;
        listCents += giftItem.price;
        lines.push({
          id: String(state.freeGiftId),
          qty: 1,
          title: giftItem.title,
          image: giftItem.image,
          group: 'gift',
          unitList: giftItem.price,
          unitPay: 0,
          isGift: true
        });
      }
    }

    /*
      Merge by variant id BEFORE pricing. The gift can collide with the same item bought as a paid accessory
      (the Geschirrtuch is both a gift and a Zubehör product), and two entries with the same merchandiseId in
      one expand operation is unspecified behaviour in the Cart Transform API — and the pricing is
      inexpressible, because fixedPricePerUnit is a single per-unit number.

      So: sum the intended cash per variant, then derive one unit price from it. payCents is then derived from
      the SAME rounded components the customer is charged on, which makes
      "displayed total === sum(components)" true by construction rather than by luck.
    */
    var merged = Object.create(null);
    for (i = 0; i < lines.length; i++) {
      var ln = lines[i];
      var slot = merged[ln.id];
      if (!slot) {
        slot = merged[ln.id] = { id: ln.id, qty: 0, cash: 0 };
      }
      slot.qty += ln.qty;
      slot.cash += ln.unitPay * ln.qty;
    }

    var components = [];
    payCents = 0;
    Object.keys(merged).forEach(function (id) {
      var m = merged[id];
      var unit = m.qty > 0 ? Math.round(m.cash / m.qty) : 0;
      payCents += unit * m.qty;
      components.push({ id: Number(id), quantity: m.qty, price: (unit / 100).toFixed(2) });
    });

    return {
      boxCount: boxCount,
      tier: tier,
      next: next,
      gift: gift,
      lines: lines,
      components: components,
      listCents: listCents,
      payCents: payCents,
      saveCents: Math.max(0, listCents - payCents),
      valid: boxCount >= (CFG.minBoxes || 1)
    };
  }

  /* ── Copy ─────────────────────────────────────────────────────────────────────────────────────────────── */

  /*
    ONE price frame everywhere: the rung price. The earlier marginal framing ("Die 4. Box kostet nur 19,90")
    was arithmetically true but contradicted the rail's "ab 4 · 22,90" on the same screen — two answers to
    two different questions. Every surface now quotes the same per-box number the customer will actually pay.
  */
  function nextRung(view) {
    if (!view.next) return null;
    var need = view.next.min - view.boxCount;
    if (need < 1) return null;
    return { need: need, min: view.next.min, unit: Math.max(0, (CFG.boxListCents || 0) - (view.next.offCents || 0)) };
  }

  function euroBadge(offCents) {
    var euros = offCents / 100;
    return '−' + (Number.isInteger(euros) ? euros : euros.toFixed(2).replace('.', ',')) + ' €';
  }

  function progressPct(view) {
    var prev = view.tier ? view.tier.min : 0;
    var target = null;
    if (view.next) target = view.next.min;
    else if (CFG.gift && CFG.gift.minBoxes > 0 && view.boxCount < CFG.gift.minBoxes) {
      target = CFG.gift.minBoxes;
    }
    if (target === null) return 100;
    var span = target - prev;
    return span > 0 ? Math.min(100, Math.round(((view.boxCount - prev) / span) * 100)) : 0;
  }

  /*
    The message ladder, in customer order:
      0–3 boxes   countdown to the price rung ("Nog N dozen: allemaal maar € 21,90 per doos")
      4–5 boxes   countdown to the free pick ("Nog N dozen: Gratis accessoires/proefjes naar jouw keuze")
      6+, no pick "Kies nu je gratis accessoires/proefjes" — an open loop until they choose
      6+, picked  "Beste prijs · 21,90 € per doos" — nudging stops; pushing past the ceiling reads as greed
  */
  function messageFor(view) {
    var g = CFG.gift;
    var giftOn = g && g.minBoxes > 0;
    var giftAhead = giftOn && view.boxCount < g.minBoxes;
    var r = nextRung(view);

    if (r && (!giftAhead || r.min <= g.minBoxes)) {
      var msg = 'Noch ' + r.need + (r.need === 1 ? ' Box' : ' Dozen') +
        ': allemaal maar ' + fmt(r.unit) + ' per doos';
      if (giftAhead && g.minBoxes === r.min) msg += ' + Gratis accessoires/proefjes';
      return msg;
    }

    if (giftAhead) {
      var needG = g.minBoxes - view.boxCount;
      return 'Noch ' + needG + (needG === 1 ? ' Box' : ' Dozen') + ': Gratis accessoires/proefjes naar keuze';
    }

    if (giftOn && !view.gift) {
      return 'Kies nu je gratis accessoires/proefjes';
    }

    if (view.tier) {
      var per = Math.max(0, (CFG.boxListCents || 0) - (view.tier.offCents || 0));
      return 'Beste prijs · ' + fmt(per) + ' per doos';
    }
    return 'Kies je favoriete smaken';
  }

  /* ── Render ───────────────────────────────────────────────────────────────────────────────────────────── */

  function render() {
    var view = compute();

    // Sparleiste
    if (el.message) el.message.textContent = messageFor(view);
    if (el.count) {
      el.count.textContent = view.boxCount > 0
        ? view.boxCount + (view.boxCount === 1 ? ' Box' : ' Dozen')
        : 'mind. ' + (CFG.minBoxes || 1) + ' Smaak';
      el.count.classList.toggle('is-on', view.boxCount > 0);
    }
    if (el.progress) el.progress.style.width = progressPct(view) + '%';

    // Ladder rail
    el.rungs.forEach(function (rung) {
      var min = parseInt(rung.getAttribute('data-jb-mm-rung'), 10) || 0;
      var activeMin = view.tier ? view.tier.min : 0;
      var isGiftRung = rung.classList.contains('jb-mm__rung--gift');
      var reached = isGiftRung ? (CFG.gift && view.boxCount >= CFG.gift.minBoxes) : min === activeMin;
      rung.classList.toggle('is-active', !!reached);
      rung.classList.toggle('is-next', !reached && !!view.next && min === view.next.min);
    });

    // Cards
    var r = nextRung(view);
    var nearMiss = r && r.need === 1 ? r.unit : null;

    el.cards.forEach(function (card) {
      var id = card.getAttribute('data-jb-mm-item');
      var qty = state.qty[id] || 0;
      var item = CFG.items[id];
      var stepper = card.querySelector('[data-jb-mm-stepper]');
      var addBtn = card.querySelector('.jb-mm-card__add');
      var qtyEl = card.querySelector('[data-jb-mm-qty]');
      var wasEl = card.querySelector('[data-jb-mm-was]');
      var nowEl = card.querySelector('[data-jb-mm-now]');

      /*
        Gift chooser takes over the ctrl slot: while no gift is chosen, every eligible card's primary
        action IS "GRATIS kiezen" (tapping it can only be a better deal than buying). Once a gift is
        chosen, all other cards revert to normal buying; the chosen card locks green until unpicked.
      */
      var isChosenGift = state.freeGiftId === id && giftUnlocked(view.boxCount) && isGiftEligible(item);
      var giftBtn = card.querySelector('[data-jb-mm-giftpick]');
      var giftSlot = false;
      if (giftBtn) {
        var pickable = giftUnlocked(view.boxCount) && isGiftEligible(item);
        giftSlot = pickable && (isChosenGift || (qty === 0 && !state.freeGiftId));
        giftBtn.hidden = !giftSlot;
        if (giftSlot) {
          giftBtn.classList.toggle('is-chosen', isChosenGift);
          giftBtn.setAttribute('aria-pressed', isChosenGift ? 'true' : 'false');
          giftBtn.textContent = isChosenGift ? '✓ Gratis gekozen' : 'GRATIS kiezen';
        }
      }

      card.classList.toggle('is-selected', qty > 0 || isChosenGift);

      if (stepper) stepper.hidden = qty < 1 || giftSlot;
      if (addBtn) {
        addBtn.hidden = qty > 0 || giftSlot;
        addBtn.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
      }
      if (qtyEl) qtyEl.textContent = String(qty);

      var dec = card.querySelector('[data-jb-mm-dec]');
      if (dec) dec.disabled = qty < 1;

      // Per-card price reflects the live tier.
      if (item && nowEl) {
        var unit = unitPayCents(item, view.tier, view.boxCount);
        // One box short of the rung: the card's price row promises what the pill promises — add this box
        // and every box (this one included) costs the rung price.
        if (item.group === 'flavor' && nearMiss !== null) unit = nearMiss;
        // Any card whose primary action is "GRATIS kiezen" prices as what that tap delivers: free.
        if (giftSlot) unit = 0;
        nowEl.textContent = fmt(unit);
        var discounted = unit < item.price;
        card.classList.toggle('is-discounted', discounted);
        if (wasEl) {
          wasEl.hidden = !discounted;
          wasEl.textContent = fmt(item.price);
        }
        var dealEl = card.querySelector('[data-jb-mm-deal]');
        if (dealEl) {
          dealEl.hidden = !discounted;
          if (discounted) {
            dealEl.textContent = giftSlot
              ? '−100 %'
              : item.group === 'flavor'
                ? euroBadge(item.price - unit)
                : '−' + Math.round((1 - unit / item.price) * 100) + ' %';
          }
        }
      }

      // Near-miss: one priced invitation per unselected flavour card.
      var label = card.querySelector('[data-jb-mm-add-label]');
      var isFlavour = card.getAttribute('data-group') === 'flavor';
      var showNear = nearMiss !== null && isFlavour && qty === 0 && !card.classList.contains('is-soldout');
      card.classList.toggle('is-nearmiss', !!showNear);
      if (label) label.textContent = showNear ? '+ Slechts ' + fmt(nearMiss) : '+ Toevoegen';
    });

    // Add-on ladder banners (rendered in both the Zubehör and Proben steps)
    if (el.gates.length) {
      var activePct = addonPctFor(view.boxCount);
      var nextA = nextAddonTier(view.boxCount);
      var maxPct = ADDON_TIERS.length ? ADDON_TIERS[ADDON_TIERS.length - 1].pct : 0;
      var gateMsg = '';
      if (!ADDON_TIERS.length) {
        gateMsg = null;
      } else if (!activePct) {
        // State the whole ladder upfront — a promise is more credible in full than as a teaser.
        gateMsg = ADDON_TIERS.map(function (t, idx) {
          return (idx === 0 ? 'Ab ' + t.min + ' ' + boxWord(t.min) : 'ab ' + t.min) + ' −' + t.pct + ' %';
        }).join(' · ');
      } else if (nextA) {
        gateMsg = '−' + activePct + ' % aktiv · ab ' + nextA.min + ' ' + boxWord(nextA.min) + ': −' + nextA.pct + ' %';
      } else {
        gateMsg = '−' + activePct + ' % op accessoires & proefjes actief';
      }
      el.gates.forEach(function (gate) {
        if (gateMsg === null) { gate.hidden = true; return; }
        gate.hidden = false;
        gate.classList.toggle('is-on', activePct > 0);
        var txt = gate.querySelector('[data-jb-mm-gate-text]');
        if (txt) txt.textContent = gateMsg;
      });
    }

    // Nav badges: the unlocked add-on discount, visible from anywhere on the page.
    var navPct = addonPctFor(view.boxCount);
    el.navBadges.forEach(function (badge) {
      badge.hidden = navPct <= 0;
      badge.textContent = '−' + navPct + ' %';
    });

    maybeToast(view);
    renderLines(view);
    renderSlots(view);
    renderDock(view);
    persist();
  }

  /*
    Per-unit tile strip, Holy-style: 2× the same flavor renders as 2 tiles, each with its own remove
    control, so the summary is editable in place. The gift tile gets a GRATIS badge instead of an X (it is
    tier-granted — removing it would just bring it back). Discounted units carry a small saving badge.
    Ghost slots with a winking juice carton prime the "box filling up" metaphor before the first tap.
  */
  function slotBadgeText(line) {
    if (line.isGift) return 'GRATIS';
    if (line.unitPay < line.unitList) {
      return line.group === 'flavor'
        ? '−' + Math.round((line.unitList - line.unitPay) / 100) + ' €'
        : '−' + Math.round((1 - line.unitPay / line.unitList) * 100) + ' %';
    }
    return '';
  }

  function slotTile(line) {
    var tile = document.createElement('div');
    tile.className = 'jb-mm__slot' + (line.isGift ? ' jb-mm__slot--gift' : '');

    if (line.image) {
      var img = document.createElement('img');
      img.src = line.image;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      tile.appendChild(img);
    }

    var badgeText = slotBadgeText(line);
    if (badgeText) {
      var badge = document.createElement('span');
      badge.className = 'jb-mm__slot-badge';
      badge.textContent = badgeText;
      tile.appendChild(badge);
    }

    if (!line.isGift) {
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'jb-mm__slot-x';
      x.setAttribute('data-jb-mm-slot-remove', line.id);
      x.setAttribute('aria-label', 'Entfernen: ' + line.title);
      x.innerHTML = '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true" focusable="false">' +
        '<path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>';
      tile.appendChild(x);
    }

    return tile;
  }

  /*
    Unlock toast for the add-on ladder. Fires only on genuine upward crossings (baseline is set silently on
    the first render, so a restored session never toasts on load) and once per rung per page session, so
    stepping 1→0→1 does not nag twice.
  */
  var prevAddonPct = null;
  var toastShownFor = Object.create(null);
  var toastTimer = null;

  function maybeToast(view) {
    var pct = addonPctFor(view.boxCount);
    if (prevAddonPct !== null && pct > prevAddonPct && !toastShownFor[pct] && el.toast) {
      toastShownFor[pct] = true;
      el.toastTitle.textContent = '−' + pct + ' % op accessoires & proefjes vrijgespeeld!';
      var nextA = nextAddonTier(view.boxCount);
      var g = CFG.gift;
      if (nextA) {
        el.toastSub.textContent = 'Ab ' + nextA.min + ' ' + boxWord(nextA.min) + ': −' + nextA.pct + ' %';
      } else if (g && g.minBoxes > 0 && view.boxCount < g.minBoxes) {
        el.toastSub.textContent = 'Ab ' + g.minBoxes + ' dozen: gratis accessoires/proefjes naar keuze';
      } else {
        el.toastSub.textContent = '';
      }
      el.toast.hidden = false;
      el.toast.classList.add('is-in');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        el.toast.classList.remove('is-in');
        el.toast.hidden = true;
      }, 4200);
    }
    prevAddonPct = pct;
  }

  function renderSlots(view) {
    if (!el.slots) return;

    // One entry per UNIT, in the order the customer added them (state.order), gift last. Chronological
    // order means a new unit always lands at the END of the row — nothing before it ever moves.
    var lineById = Object.create(null);
    view.lines.forEach(function (line) {
      if (!line.isGift) lineById[line.id] = line;
    });
    var units = [];
    state.order.forEach(function (id) {
      if (lineById[id]) units.push(lineById[id]);
    });
    view.lines.forEach(function (line) {
      if (line.isGift) units.push(line);
    });

    /*
      Holy's exact row logic: always exactly MAX_SLOTS tiles.
      - up to 5 units: unit tiles, ghosts fill the rest
      - 6+ units: the first 4 units, then the 5th tile becomes the ALLES ANZEIGEN button

      KEYED DIFF, not a rebuild. Rebuilding on every tap recreated all five nodes — images redecoded and the
      whole row visibly jumped with each add. Every slot position gets a signature of what it should show;
      a DOM node is only touched when its signature actually changed, so adding the 4th box leaves tiles
      1-3 as the exact same elements. On Holy the row holds still; now ours does too.
    */
    var totalUnits = units.length;
    var plan = [];

    if (totalUnits <= MAX_SLOTS) {
      units.forEach(function (line) {
        plan.push({ sig: 'u:' + line.id + ':' + (line.isGift ? 'g' : '') + ':' + slotBadgeText(line), line: line });
      });
      for (var i = totalUnits; i < MAX_SLOTS; i++) plan.push({ sig: 'ghost' });
    } else {
      units.slice(0, MAX_SLOTS - 1).forEach(function (line) {
        plan.push({ sig: 'u:' + line.id + ':' + (line.isGift ? 'g' : '') + ':' + slotBadgeText(line), line: line });
      });
      plan.push({ sig: 'all' });
    }

    plan.forEach(function (want, idx) {
      var existing = el.slots.children[idx];
      if (existing && existing.getAttribute('data-sig') === want.sig) {
        if (want.sig === 'all') existing.setAttribute('aria-label', 'Alle ' + totalUnits + ' Artikel bekijken');
        return; // untouched — no repaint, no jump
      }

      var node;
      if (want.sig === 'ghost') {
        node = el.ghostTpl
          ? el.ghostTpl.content.firstElementChild.cloneNode(true)
          : document.createElement('span');
      } else if (want.sig === 'all') {
        node = document.createElement('button');
        node.type = 'button';
        node.className = 'jb-mm__slot jb-mm__slot-all';
        node.setAttribute('data-jb-mm-open-sheet', '');
        node.setAttribute('aria-label', 'Alle ' + totalUnits + ' Artikel bekijken');
        node.textContent = 'Alles bekijken';
      } else {
        node = slotTile(want.line);
      }
      node.setAttribute('data-sig', want.sig);

      if (existing) el.slots.replaceChild(node, existing);
      else el.slots.appendChild(node);
    });

    while (el.slots.children.length > plan.length) {
      el.slots.removeChild(el.slots.lastElementChild);
    }
  }

  function lineMarkup(line) {
    var li = document.createElement('li');
    li.className = 'jb-mm__line' + (line.isGift ? ' jb-mm__line--gift' : '');

    if (line.image) {
      var img = document.createElement('img');
      img.src = line.image;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 40;
      img.height = 40;
      li.appendChild(img);
    }

    var name = document.createElement('span');
    name.className = 'jb-mm__line-name';
    name.textContent = line.title;
    li.appendChild(name);

    if (!line.isGift) {
      // Quantities are editable right here — matching the per-unit strip, so nobody has to hunt for the
      // original card in the grid to change their mind.
      var step = document.createElement('span');
      step.className = 'jb-mm__line-step';

      var dec = document.createElement('button');
      dec.type = 'button';
      dec.setAttribute('data-jb-mm-line-dec', line.id);
      dec.setAttribute('aria-label', line.title + ': Aantal verlagen');
      dec.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">' +
        '<path d="M5 12h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

      var qty = document.createElement('output');
      qty.textContent = String(line.qty);

      var inc = document.createElement('button');
      inc.type = 'button';
      inc.setAttribute('data-jb-mm-line-inc', line.id);
      inc.setAttribute('aria-label', line.title + ': Aantal verhogen');
      inc.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">' +
        '<path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

      step.appendChild(dec);
      step.appendChild(qty);
      step.appendChild(inc);
      li.appendChild(step);
    }

    var price = document.createElement('span');
    price.className = 'jb-mm__line-price';
    price.textContent = line.isGift ? 'GRATIS' : fmt(line.unitPay * line.qty);
    li.appendChild(price);

    return li;
  }

  function renderLines(view) {
    [el.lines, el.sheetLines].forEach(function (list) {
      if (!list) return;
      list.replaceChildren.apply(list, view.lines.map(lineMarkup));
    });

    if (el.summary) el.summary.hidden = view.lines.length === 0;
    if (el.subtotal) el.subtotal.textContent = fmt(view.listCents);
    if (el.total) el.total.textContent = fmt(view.payCents);
    if (el.saverow) el.saverow.hidden = view.saveCents <= 0;
    if (el.savings) el.savings.textContent = '−' + fmt(view.saveCents);

    if (el.savepill) {
      if (view.saveCents > 0 && view.listCents > 0) {
        var pct = Math.round((view.saveCents / view.listCents) * 100);
        el.savepill.hidden = false;
        el.savepill.textContent = 'Je bespaart ' + fmt(view.saveCents) + ' (' + pct + ' %)';
      } else {
        el.savepill.hidden = true;
      }
    }
  }

  function renderDock(view) {
    if (el.dockTotal) el.dockTotal.textContent = fmt(view.payCents);
    if (el.dockCompare) {
      el.dockCompare.hidden = view.saveCents <= 0;
      el.dockCompare.textContent = fmt(view.listCents);
    }
    if (el.dockNudge) {
      el.dockNudge.textContent = messageFor(view);
    }
    if (el.dockProgress) el.dockProgress.style.width = progressPct(view) + '%';
    if (el.dockSave) {
      el.dockSave.hidden = view.saveCents <= 0;
      el.dockSave.textContent = '−' + fmt(view.saveCents);
    }
    if (el.sheetTotal) el.sheetTotal.textContent = fmt(view.payCents);
    if (el.sheetCompare) {
      el.sheetCompare.hidden = view.saveCents <= 0;
      el.sheetCompare.textContent = fmt(view.listCents);
    }
    if (el.sheetSave) {
      el.sheetSave.hidden = view.saveCents <= 0;
      if (view.saveCents > 0 && view.listCents > 0) {
        el.sheetSave.textContent =
          'Je bespaart ' + fmt(view.saveCents) +
          ' (' + Math.round((view.saveCents / view.listCents) * 100) + ' %)';
      }
    }

    el.ctas.forEach(function (cta) {
      cta.setAttribute('aria-disabled', view.valid && !inFlight ? 'false' : 'true');
    });

    // Near-miss row inside the sheet — the single place friction is spent.
    if (el.nearmiss) {
      var rn = nextRung(view);
      var show = view.valid && rn && rn.need <= 2 && !sessionStorage.getItem(STORAGE_KEY + ':nudged');
      if (show) {
        el.nearmiss.hidden = false;
        el.nearmiss.replaceChildren();
        var txt = document.createElement('span');
        txt.textContent = 'Bijna gelukt — ' + (rn.need === 1 ? 'één doos' : rn.need + ' Dozen') +
          ' meer, dan kost elke doos maar ' + fmt(rn.unit) + '.';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = '+' + rn.need + ' Box' + (rn.need > 1 ? 'en' : '');
        btn.addEventListener('click', function () {
          topUp(rn.need);
          sessionStorage.setItem(STORAGE_KEY + ':nudged', '1');
        });
        el.nearmiss.appendChild(txt);
        el.nearmiss.appendChild(btn);
      } else {
        el.nearmiss.hidden = true;
      }
    }
  }

  /* ── Mutations ────────────────────────────────────────────────────────────────────────────────────────── */

  function boxTotal() {
    var n = 0;
    Object.keys(state.qty).forEach(function (id) {
      var it = CFG.items[id];
      if (it && it.group === 'flavor') n += state.qty[id];
    });
    return n;
  }

  function setQty(id, qty) {
    var item = CFG.items[id];
    if (!item || !item.available) return;

    qty = Math.max(0, qty);

    if (item.group === 'flavor' && qty > (state.qty[id] || 0)) {
      var cap = CFG.maxBoxes || 24;
      if (boxTotal() + (qty - (state.qty[id] || 0)) > cap) {
        announce('Maximal ' + cap + ' dozen. Voor grotere bestellingen: bestellung@juice-box.at');
        return;
      }
    }

    var current = state.qty[id] || 0;
    if (qty > current) {
      for (var a = current; a < qty; a++) state.order.push(id);
    } else if (qty < current) {
      // remove from the END — all tiles of a variant are identical, so trimming the last is invisible
      for (var d = current; d > qty; d--) {
        var at = state.order.lastIndexOf(id);
        if (at !== -1) state.order.splice(at, 1);
      }
    }

    if (qty === 0) delete state.qty[id];
    else state.qty[id] = qty;

    render();
  }

  // Auto-picks the customer's most-added flavour so the offer requires no decision.
  function topUp(need) {
    var best = null;
    var bestQty = 0;
    Object.keys(state.qty).forEach(function (id) {
      var it = CFG.items[id];
      if (it && it.group === 'flavor' && state.qty[id] > bestQty) {
        bestQty = state.qty[id];
        best = id;
      }
    });
    if (!best) {
      var firstCard = root.querySelector('[data-group="flavor"]:not(.is-soldout)');
      if (firstCard) best = firstCard.getAttribute('data-jb-mm-item');
    }
    if (best) setQty(best, (state.qty[best] || 0) + need);
  }

  function announce(msg) {
    if (el.live) el.live.textContent = msg;
  }

  /* ── Persistence ──────────────────────────────────────────────────────────────────────────────────────── */

  var persistTimer = null;
  function persist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(function () {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ qty: state.qty, order: state.order, freeGiftId: state.freeGiftId }));
      } catch (err) {
        /* private mode / quota — the builder still works, it just will not survive a reload */
      }
    }, 200);
  }

  function restore() {
    var raw;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch (err) {
      return false;
    }
    if (!raw) return false;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (err) {
      return false;
    }
    if (!saved || !saved.qty) return false;

    var restored = 0;
    state.order = [];
    state.freeGiftId = null;
    if (saved.freeGiftId && isGiftEligible(CFG.items[saved.freeGiftId]) && !state.qty[saved.freeGiftId]) {
      state.freeGiftId = saved.freeGiftId;
    }
    // Validate every id against what Liquid actually rendered — a variant may since have sold out or been
    // unpublished. Drop unknown ids silently rather than posting a dead id to /cart/add.
    Object.keys(saved.qty).forEach(function (id) {
      var item = CFG.items[id];
      if (!item || !item.available) return;
      var qty = parseInt(saved.qty[id], 10);
      if (!qty || qty < 1) return;
      state.qty[id] = qty;
      restored++;
    });

    // Rebuild the unit order: prefer the saved sequence (validated), fall back to grouped.
    var counts = Object.create(null);
    if (Array.isArray(saved.order)) {
      saved.order.forEach(function (id) {
        if (!state.qty[id]) return;
        counts[id] = (counts[id] || 0) + 1;
        if (counts[id] <= state.qty[id]) state.order.push(id);
      });
    }
    Object.keys(state.qty).forEach(function (id) {
      for (var have = counts[id] || 0; have < state.qty[id]; have++) state.order.push(id);
    });

    return restored > 0;
  }

  /* ── Cart ─────────────────────────────────────────────────────────────────────────────────────────────── */

  function buildProperties(view) {
    // Already merged by variant id and rounded in compute() — this is the exact set payCents was derived from.
    var components = view.components;

    /*
      snippets/line-item.liquid splits the image URL on "," when it renders the drawer thumbnails, so any
      comma anywhere in the URL truncates it into a broken image. Skip those rather than ship a broken chip.
      Images are already CDN-sized at width=100 — the shared snippet applies no image_url transform of its own.
    */
    var seenThumb = Object.create(null);
    var thumbs = view.lines
      .filter(function (l) {
        if (!l.image || l.image.indexOf(',') !== -1) return false;
        if (seenThumb[l.id]) return false; // gift can repeat a paid item
        seenThumb[l.id] = true;
        return true;
      })
      .slice(0, MAX_THUMBS)
      .map(function (l) { return { image: l.image, qty: l.qty }; });

    var props = {
      _bundleComponents: JSON.stringify(components),
      _bundleItem: JSON.stringify(thumbs),
      _bundleTitle: 'Jouw Mix & Match Set'
    };

    /*
      The cart transform prices via fixedPricePerUnit, which leaves original_line_price === final_line_price,
      so the drawer can never derive a strikethrough on its own (verified against the live cart). Ship the
      undiscounted total in cents; snippets/line-item.liquid renders it, guarded on this property alone.
    */
    if (view.listCents > view.payCents) {
      props._bundleCompareAt = String(view.listCents);
    }

    return props;
  }

  function collectSections() {
    var sections = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  var interceptBypass = false;
  var pendingTrigger = null;

  function shouldIntercept(view) {
    if (interceptBypass) return false;
    if (!addonPctFor(view.boxCount)) return false;
    if (sessionStorage.getItem(STORAGE_KEY + ':addon-nudged')) return false;
    // anything from steps 2/3 (paid or the chosen gift) counts as engagement — no popup then
    return !view.lines.some(function (l) { return l.group !== 'flavor'; });
  }

  function openIntercept(view) {
    if (!el.intercept) return;
    try { sessionStorage.setItem(STORAGE_KEY + ':addon-nudged', '1'); } catch (err) { /* noop */ }
    var pct = addonPctFor(view.boxCount);
    el.interceptTitle.textContent = 'Je hebt −' + pct + ' % op accessoires & proefjes vrijgespeeld!';
    el.interceptText.textContent = 'Je korting is al vrijgeschakeld – wil je er nog iets passends bij leggen?';
    if (el.sheet && el.sheet.open) el.sheet.close();
    el.intercept.showModal();
  }

  async function addToCart(trigger) {
    if (inFlight) return;

    var view = compute();

    if (!view.valid) {
      trigger.classList.add('is-shaking');
      setTimeout(function () { trigger.classList.remove('is-shaking'); }, 500);
      announce('Kies minstens één smaak.');
      var grid = root.querySelector('[data-jb-mm-grid="flavor"]');
      if (grid) scrollToEl(grid);
      return;
    }

    if (shouldIntercept(view)) {
      pendingTrigger = trigger;
      openIntercept(view);
      return;
    }
    interceptBypass = false;

    // Re-validate against what is still available, in case a variant sold out since page render.
    var dropped = [];
    Object.keys(state.qty).forEach(function (id) {
      var item = CFG.items[id];
      if (!item || !item.available) {
        dropped.push(item ? item.title : id);
        delete state.qty[id];
      }
    });
    if (dropped.length) {
      render();
      announce('Niet meer beschikbaar en verwijderd: ' + dropped.join(', '));
      return;
    }

    var props = buildProperties(view);

    // A truncated _bundleComponents is exactly the "malformed JSON" case the function skips — which would
    // silently sell the parent at its admin price. Refuse to submit rather than rely on that guardrail.
    if (props._bundleComponents.length > MAX_PROPERTY_CHARS) {
      announce('Je doos is te groot. Verwijder een paar artikelen.');
      return;
    }

    inFlight = true;
    scrollBeforeAdd = window.scrollY;
    el.ctas.forEach(function (cta) {
      cta.setAttribute('aria-busy', 'true');
      cta.setAttribute('aria-disabled', 'true');
      var label = cta.querySelector('[data-jb-mm-cta-label]');
      if (label && cta.dataset.labelBusy) label.textContent = cta.dataset.labelBusy;
    });

    var controller = new AbortController();
    var timeout = setTimeout(function () { controller.abort(); }, 15000);

    try {
      var sections = collectSections();
      var res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
        body: JSON.stringify({
          items: [{ id: CFG.parentVariantId, quantity: 1, properties: props }],
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      });

      var json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || 'cart/add failed');

      var cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
      cart.sections = json.sections;

      // Selection is only cleared once the add is confirmed.
      state.qty = Object.create(null);
      state.order = [];
      state.freeGiftId = null;
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (err) { /* noop */ }
      render();

      // Exact detail shapes the theme's shared modules expect — a wrong shape poisons cart state in theme.js.
      trigger.dispatchEvent(
        new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [], cart: cart } })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } })
      );

      if (el.sheet && el.sheet.open) el.sheet.close();
    } catch (err) {
      console.error('[jb-mix-and-match] add to cart failed', err);
      announce('Dat is helaas niet gelukt. Probeer het nog een keer.');
    } finally {
      clearTimeout(timeout);
      inFlight = false;
      el.ctas.forEach(function (cta) {
        cta.removeAttribute('aria-busy');
        var label = cta.querySelector('[data-jb-mm-cta-label]');
        if (label && cta.dataset.labelIdle) label.textContent = cta.dataset.labelIdle;
      });
      render();
    }
  }

  /* ── Step nav ─────────────────────────────────────────────────────────────────────────────────────────── */

  var navLocked = false;
  var navLockTimer = null;

  function setActiveNav(stepNo) {
    el.navItems.forEach(function (item) {
      var on = item.getAttribute('data-jb-mm-nav') === String(stepNo);
      item.classList.toggle('is-active', on);
      item.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  /*
    Scroll-spy. The whole sticky block (nav + Sparleiste) sits under the theme header, so a step counts as
    "current" once its top passes just below that combined chrome — otherwise the highlight flips a full
    viewport early.
  */
  function spyScroll() {
    if (navLocked || !el.steps.length) return;
    var chrome = (el.sparleiste ? el.sparleiste.getBoundingClientRect().bottom : 0) + 8;
    var current = el.steps[0];
    el.steps.forEach(function (step) {
      if (step.getBoundingClientRect().top <= chrome) current = step;
    });
    setActiveNav(current.getAttribute('data-jb-mm-step'));
  }

  var spyTicking = false;
  window.addEventListener(
    'scroll',
    function () {
      if (spyTicking) return;
      spyTicking = true;
      requestAnimationFrame(function () {
        spyScroll();
        spyTicking = false;
      });
    },
    { passive: true }
  );

  /* ── Scrolling ────────────────────────────────────────────────────────────────────────────────────────── */

  /*
    Never a bare scrollIntoView(): the theme measures --header-height once at parse time, before the webfont
    swaps, so the stored value is stale. Measure the header live instead.
  */
  /*
    Own rAF easing, plus a guaranteed landing.

    Animation frames are suspended whenever the document is hidden (backgrounded tab, app switch, the
    browser pane losing focus) — and so is native `behavior: 'smooth'`. Without a fallback the customer
    taps a step, switches away, comes back, and is still at the top with nothing having happened.

    So: animate when we can, and always arm a timer that snaps to the target if the animation never ran or
    was interrupted. Timers keep firing while hidden; rAF does not.
  */
  var scrollSnapTimer = null;

  function scrollWindowTo(top) {
    var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    top = Math.max(0, Math.min(Math.round(top), max));

    var start = window.scrollY;
    var delta = top - start;
    if (Math.abs(delta) < 2) return;

    clearTimeout(scrollSnapTimer);

    if (reduceMotion || document.hidden) {
      window.scrollTo(0, top);
      return;
    }

    var duration = Math.min(600, Math.max(250, Math.abs(delta) * 0.35));
    var startedAt = null;
    var done = false;

    function step(ts) {
      if (startedAt === null) startedAt = ts;
      var p = Math.min(1, (ts - startedAt) / duration);
      var eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      window.scrollTo(0, Math.round(start + delta * eased));
      if (p < 1) requestAnimationFrame(step);
      else done = true;
    }

    requestAnimationFrame(step);

    scrollSnapTimer = setTimeout(function () {
      if (!done && Math.abs(window.scrollY - top) > 4) window.scrollTo(0, top);
    }, duration + 120);
  }

  function scrollToEl(target) {
    // Clear BOTH sticky layers: the theme header and our own nav + Sparleiste block. Measured live, because
    // the theme's --header-height is written once at parse time and goes stale after the webfont swaps.
    var headerH = parseInt(getComputedStyle(el.shell).getPropertyValue('--jb-mm-sticky-top'), 10) || 0;
    var stickyH = el.sparleiste ? el.sparleiste.getBoundingClientRect().height : 0;
    scrollWindowTo(target.getBoundingClientRect().top + window.scrollY - headerH - stickyH - 12);
  }

  /* ── Events ───────────────────────────────────────────────────────────────────────────────────────────── */

  root.addEventListener('click', function (event) {
    var giftBtn = event.target.closest('[data-jb-mm-giftpick]');
    if (giftBtn) {
      var giftCard = giftBtn.closest('[data-jb-mm-item]');
      var gid = giftCard && giftCard.getAttribute('data-jb-mm-item');
      if (gid) {
        var wasChosen = state.freeGiftId === gid;
        state.freeGiftId = wasChosen ? null : gid;
        var gItem = CFG.items[gid];
        announce(wasChosen
          ? 'Gratis selectie verwijderd'
          : 'Gratis gekozen: ' + (gItem ? gItem.title : ''));
        render();
      }
      return;
    }

    var slotX = event.target.closest('[data-jb-mm-slot-remove]');
    if (slotX) {
      var sid = slotX.getAttribute('data-jb-mm-slot-remove');
      setQty(sid, (state.qty[sid] || 0) - 1);
      return;
    }

    var lineDec = event.target.closest('[data-jb-mm-line-dec]');
    var lineInc = event.target.closest('[data-jb-mm-line-inc]');
    if (lineDec || lineInc) {
      var lid = (lineDec || lineInc).getAttribute(lineDec ? 'data-jb-mm-line-dec' : 'data-jb-mm-line-inc');
      setQty(lid, (state.qty[lid] || 0) + (lineInc ? 1 : -1));
      return;
    }

    var incBtn = event.target.closest('[data-jb-mm-inc]');
    var decBtn = event.target.closest('[data-jb-mm-dec]');
    var card = event.target.closest('[data-jb-mm-item]');

    if (card && (incBtn || decBtn)) {
      var id = card.getAttribute('data-jb-mm-item');
      var current = state.qty[id] || 0;
      setQty(id, incBtn ? current + 1 : current - 1);
      return;
    }

    var cta = event.target.closest('[data-jb-mm-add]');
    if (cta) {
      addToCart(cta);
      return;
    }

    var chip = event.target.closest('[data-jb-mm-filter]');
    if (chip) {
      var filter = chip.getAttribute('data-jb-mm-filter');
      root.querySelectorAll('[data-jb-mm-filter]').forEach(function (c) {
        var on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-jb-mm-grid="flavor"] [data-jb-mm-item]').forEach(function (c) {
        c.hidden = filter !== 'all' && c.getAttribute('data-kind') !== filter;
      });
      return;
    }

    if (event.target.closest('[data-jb-mm-open-sheet]') && el.sheet) {
      el.sheet.showModal();
      return;
    }

    if (event.target.closest('[data-jb-mm-close-sheet]') && el.sheet) {
      el.sheet.close();
      return;
    }

    var nav = event.target.closest('[data-jb-mm-nav]');
    if (nav) {
      var stepNo = nav.getAttribute('data-jb-mm-nav');
      var target = root.querySelector('[data-jb-mm-step="' + stepNo + '"]');
      if (target) {
        navLocked = true; // stop the spy fighting the smooth scroll it just triggered
        setActiveNav(stepNo);
        scrollToEl(target);
        clearTimeout(navLockTimer);
        navLockTimer = setTimeout(function () { navLocked = false; }, 800);
      }
      return;
    }

    if (event.target.closest('[data-jb-mm-open-howto]') && el.howto) {
      el.howto.showModal();
      return;
    }

    if (event.target.closest('[data-jb-mm-close-howto]') && el.howto) {
      el.howto.close();
      return;
    }

    if (event.target.closest('[data-jb-mm-intercept-browse]') && el.intercept) {
      el.intercept.close();
      var step2 = root.querySelector('[data-jb-mm-step="2"]');
      if (step2) {
        navLocked = true;
        setActiveNav('2');
        scrollToEl(step2);
        clearTimeout(navLockTimer);
        navLockTimer = setTimeout(function () { navLocked = false; }, 800);
      }
      return;
    }

    if (event.target.closest('[data-jb-mm-intercept-skip]') && el.intercept) {
      el.intercept.close();
      interceptBypass = true;
      if (pendingTrigger) addToCart(pendingTrigger);
      return;
    }
  });

  // Click on the ::backdrop closes both dialogs (native <dialog> reports the dialog itself as the target).
  [el.sheet, el.howto, el.intercept].forEach(function (dlg) {
    if (!dlg) return;
    dlg.addEventListener('click', function (event) {
      if (event.target === dlg) dlg.close();
    });
  });

  // Perceived-latency killer: the press feedback fires before any state maths.
  if (!reduceMotion) {
    root.addEventListener('pointerdown', function (event) {
      var card = event.target.closest('[data-jb-mm-item]');
      if (!card || card.classList.contains('is-soldout')) return;
      card.style.transform = 'scale(.97)';
      setTimeout(function () { card.style.transform = ''; }, 90);
    });
  }

  // Long-press repeat — the only realistic route to the top rung from a single card.
  var repeatTimer = null;
  var repeatInterval = null;
  function stopRepeat() {
    clearTimeout(repeatTimer);
    clearInterval(repeatInterval);
    repeatTimer = repeatInterval = null;
  }
  root.addEventListener('pointerdown', function (event) {
    var btn = event.target.closest('[data-jb-mm-inc], [data-jb-mm-dec]');
    var card = event.target.closest('[data-jb-mm-item]');
    if (!btn || !card) return;
    var id = card.getAttribute('data-jb-mm-item');
    var isInc = btn.hasAttribute('data-jb-mm-inc');
    repeatTimer = setTimeout(function () {
      repeatInterval = setInterval(function () {
        var current = state.qty[id] || 0;
        setQty(id, isInc ? current + 1 : current - 1);
      }, 120);
    }, 300);
  });
  ['pointerup', 'pointercancel', 'pointerleave', 'blur'].forEach(function (evt) {
    root.addEventListener(evt, stopRepeat, true);
  });
  window.addEventListener('blur', stopRepeat);

  /* ── Cart drawer coexistence ──────────────────────────────────────────────────────────────────────────── */

  var drawer = document.getElementById('cart-drawer');

  /*
    The dock must never sit on top of, or peek out beneath, the cart drawer.

    CSS already hides it via `html.lock` (the class this theme actually sets — NOT body.drawer-open, which
    the older pickers guard on and which nothing ever sets). This observer is the belt-and-braces layer,
    because the theme's lock is refcounted across nested dialogs and can desync.

    It is deliberately STATELESS: it recomputes visibility from the live truth on every mutation rather than
    toggling on paired show/hide events. An earlier event-pair version got permanently stuck hidden when a
    close happened without a matching `dialog:after-hide`.
  */
  function syncDockVisibility() {
    if (!el.dock) return;
    var blocked =
      document.documentElement.classList.contains('lock') ||
      (drawer && drawer.hasAttribute('open'));
    el.dock.classList.toggle('is-hidden', !!blocked);
  }

  if (el.dock) {
    new MutationObserver(syncDockVisibility).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    if (drawer) {
      new MutationObserver(syncDockVisibility).observe(drawer, {
        attributes: true,
        attributeFilter: ['open']
      });
    }
    syncDockVisibility();
  }

  if (drawer) {
    // iOS Safari's `overflow:hidden` scroll lock can reset documentElement.scrollTop. This page is several
    // viewports long, so landing back at the top after every add would break the "keep adding" premise.
    drawer.addEventListener('dialog:after-hide', function () {
      if (scrollBeforeAdd === null) return;
      var target = scrollBeforeAdd;
      scrollBeforeAdd = null;
      requestAnimationFrame(function () {
        window.scrollTo({ top: target, behavior: 'instant' });
      });
    });
  }

  /* ── Sticky shadow + dock height ──────────────────────────────────────────────────────────────────────── */

  if (el.sentinel && el.sparleiste && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      function (entries) {
        el.sparleiste.classList.toggle('is-stuck', !entries[0].isIntersecting);
      },
      { threshold: 0 }
    ).observe(el.sentinel);
  }

  /* ── Layout measurements ──────────────────────────────────────────────────────────────────────────────── */

  /*
    Two values the CSS cannot work out on its own:

    --jb-mm-bleed      distance from the section's content edge to the viewport edge, so the hero and the
                       sticky bar can run edge to edge. Measured rather than 100vw, which would overflow by
                       the scrollbar width on desktop.
    --jb-mm-sticky-top height of the theme's own sticky header, so our bar parks underneath it instead of
                       covering it. The theme writes --header-height once at parse time (sections/header.liquid),
                       before the webfont swaps, so that value is stale — measure live instead.
  */
  function measureLayout() {
    if (!el.shell) return;

    var rect = el.shell.getBoundingClientRect();
    el.shell.style.setProperty('--jb-mm-bleed', Math.max(0, Math.round(rect.left)) + 'px');

    var header = document.querySelector('.header');
    var top = 0;
    if (header) {
      var host = header.closest('.shopify-section') || header;
      var stuck = ['sticky', 'fixed'];
      if (
        stuck.indexOf(getComputedStyle(host).position) !== -1 ||
        stuck.indexOf(getComputedStyle(header).position) !== -1
      ) {
        /*
          Unrounded, minus 1: header heights are fractional on mobile DPRs, and rounding UP parked the nav
          a hair below the header — a visible 1px seam while stuck. Tucking 1px UNDER the header instead is
          invisible, because the header is opaque and stacks above the nav.
        */
        top = Math.max(0, header.getBoundingClientRect().height - 1);
      }
    }
    el.shell.style.setProperty('--jb-mm-sticky-top', top + 'px');
  }

  measureLayout();
  window.addEventListener('resize', measureLayout);
  window.addEventListener('orientationchange', measureLayout);
  window.addEventListener('load', measureLayout);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureLayout);

  if (el.dock && 'ResizeObserver' in window) {
    var shell = el.shell;
    var ro = new ResizeObserver(function (entries) {
      var h = Math.round(entries[0].target.getBoundingClientRect().height);
      // Ignore 0 — the dock measures zero whenever it is hidden behind the cart drawer, and writing that
      // through would collapse the page's bottom padding and let the last row slide under the bar on close.
      if (h > 0 && shell) shell.style.setProperty('--jb-mm-bar-h', h + 'px');
    });
    ro.observe(el.dock);
  }

  /* ── Boot ─────────────────────────────────────────────────────────────────────────────────────────────── */

  function boot() {
    var restored = restore();
    render();
    if (restored) announce('Je selectie van net is er weer.');
  }

  boot();

  // bfcache does not cover the iOS discard-and-reload path, so rehydrate there too.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) return;
    if (!Object.keys(state.qty).length && restore()) render();
  });
})();
